import { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import VendorBrowser from "../marketplace/VendorBrowser";
import { callAI, track } from "../lib/api";
import { renderMD } from "../lib/markdown";
import { getStageState } from "../lib/journey";

// Stage 9 — Open house.
//
// Two paths the seller can choose between:
//
//   (A) DIY    — pick a date; AI generates marketing pack; seller hosts.
//   (B) Assist — pick a marketplace `listing_assistant`; they host on the
//                seller's behalf and the seller gets a notifications feed
//                (visitors, feedback summaries).
//
// The choice lives in stage data as `mode: "diy" | "assisted"`. Either can
// be re-chosen later by hitting the toggle at the top.

const SYSTEM = "You write event-marketing copy for real estate open houses. Tight, specific, no clichés.";

const PROMPT = (address, date, headline, note) => `Open house for: ${address}
Date: ${date}
Listing headline: ${headline || "(none provided)"}
${note ? `Seller's guidance for this regeneration — factor this in: ${note}` : ""}

Output in markdown with these exact sections:

**Instagram caption** (1-2 sentences + 3 hashtags)

**Email blast subject + body** (subject under 50 chars; body 3-4 short lines)

**Door-hanger / postcard text** (one paragraph, max 60 words)`;

const nextSaturday = (offsetDays = 7) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  while (d.getDay() !== 6) d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

// Mock visitor activity used when the seller picks an assisted pro and is
// waiting for the open-house event to happen.
const MOCK_VISITS = [
  { name: "Visitor #A — couple, 30s", note: "Asked about HVAC age and HOA dues", sentiment: "warm" },
  { name: "Visitor #B — solo buyer",  note: "Spent ~22 min, took measurements upstairs", sentiment: "hot" },
  { name: "Visitor #C — family of 4", note: "Walked through quickly; said price felt high", sentiment: "cool" },
];

const sentimentColor = { hot: "#c0354a", warm: "#c47c1a", cool: "#7aad98" };

export default function OpenHouseStage({ state, journey, update }) {
  const mode = state.data?.mode || null; // null | "diy" | "assisted"

  const pickMode = (m) => {
    track("seller_open_house_mode_picked", { mode: m });
    update({ data: { ...(state.data || {}), mode: m } });
  };

  if (!mode) {
    return (
      <>
        <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 18px" }}>
          How would you like to handle the open house?
        </p>
        <div className="s-confirm-actions">
          <button type="button" className="s-confirm-btn primary" onClick={() => pickMode("diy")}>
            <span>Host it myself · pick a date, get AI marketing</span>
            <span className="s-confirm-btn-arrow">→</span>
          </button>
          <button type="button" className="s-confirm-btn ghost" onClick={() => pickMode("assisted")}>
            <span>Have a marketplace pro host it on my behalf</span>
            <span className="s-confirm-btn-arrow">→</span>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="s-ai-pill" style={{ margin: 0 }}>
          {mode === "diy" ? "★ AI · Marketing pack generation" : "★ Marketplace · Assisted hosting"}
        </div>
        <button type="button" className="s-link-btn" onClick={() => pickMode(null)}>
          ↻ switch mode
        </button>
      </div>
      {mode === "diy" ? <DIYView state={state} journey={journey} update={update} /> : <AssistedView state={state} journey={journey} update={update} />}
    </>
  );
}

// ─── DIY ────────────────────────────────────────────────────────────────────
function DIYView({ state, journey, update }) {
  const [date, setDate] = useState(state.data?.date || nextSaturday());
  const [loading, setLoading] = useState(false);
  const [marketing, setMarketing] = useState(state.data?.marketing || "");
  const [err, setErr] = useState(null);

  const generate = async (note = "") => {
    if (!date) return;
    setLoading(true);
    setErr(null);
    track("seller_open_house_marketing_run", { date, has_note: Boolean(note) });
    try {
      const headline = getStageState(journey, "listing").data?.headline || "";
      const { text } = await callAI({
        system: SYSTEM,
        prompt: PROMPT(journey.address, date, headline, note),
        maxTokens: 800,
      });
      setMarketing(text);
      update({
        status: "done",
        data: { ...(state.data || {}), mode: "diy", date, marketing: text, scheduled_at: new Date().toISOString() },
      });
      track("seller_open_house_scheduled", { date });
    } catch {
      setErr("Couldn't generate marketing copy. Try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!state.data?.date) update({ data: { ...(state.data || {}), date } });
  }, []);

  return (
    <>
      <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 14px" }}>
        Pick a Saturday. We send invites to our buyer list, post on Zillow/Redfin event pages,
        place yard signage, and handle the lockbox + sign-in.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); update({ data: { ...(state.data || {}), date: e.target.value } }); }}
          className="s-intake-input"
          style={{ maxWidth: 200, minWidth: 0 }}
        />
        {!marketing && (
          <button type="button" className="s-btn amber" onClick={() => generate()} disabled={!date || loading}>
            {loading ? "Generating…" : "Schedule & generate marketing"}
          </button>
        )}
      </div>

      {marketing && (
        <RegenerateBar
          onRegenerate={(note) => generate(note)}
          loading={loading}
          placeholder="e.g. more casual tone, mention the open floor plan…"
        />
      )}

      {loading && <Spinner label="Writing your open-house marketing pack…" />}
      {err && !loading && <div className="s-intake-err" style={{ marginTop: 12 }}>{err}</div>}
      {marketing && !loading && <div className="s-prose" dangerouslySetInnerHTML={{ __html: renderMD(marketing) }} />}
    </>
  );
}

// ─── Assisted ───────────────────────────────────────────────────────────────
function AssistedView({ state, journey, update }) {
  const hasPickedPro = Boolean(state.data?.picked_vendor_id && state.data?.quote);
  const isAccepted = Boolean(state.data?.accepted_at);
  const isPostEvent = isAccepted && state.data?.event_complete; // mock flag for now

  return (
    <>
      <p style={{ color: "#5b7365", fontSize: 14, lineHeight: 1.7, margin: "0 0 14px" }}>
        Pick a marketplace pro. They'll schedule the open house, host it, and send you a visitor
        summary afterward. You don't need to show up.
      </p>

      <VendorBrowser
        type="listing_assistant"
        journey={journey}
        state={state}
        update={update}
        stageId="openhouse"
      />

      {isAccepted && (
        <div className="s-soon" style={{ marginTop: 18 }}>
          <div className="s-soon-row">
            <div className="s-soon-bullet">✓</div>
            <div className="s-soon-text">
              <strong>{state.data.picked_vendor_name}</strong> will host the open house
              and send you a visitor summary afterward.
            </div>
          </div>
        </div>
      )}

      {/* Mock activity feed — what the seller would see after the event. */}
      {isAccepted && (
        <>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.08em", margin: "22px 0 8px", textTransform: "uppercase" }}>
            Visitor activity {isPostEvent ? "" : "(sample · live data lands post-event)"}
          </div>
          {MOCK_VISITS.map((v) => (
            <div key={v.name} className="s-vendor passive" style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                <div className="s-vendor-name">{v.name}</div>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: sentimentColor[v.sentiment], fontWeight: 700 }}>
                  {v.sentiment}
                </span>
              </div>
              <div className="s-vendor-meta" style={{ fontFamily: "'Outfit',sans-serif", letterSpacing: 0, color: "#5b7365", marginTop: 4, lineHeight: 1.5 }}>
                {v.note}
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
